export type PollTimerRef = { current: ReturnType<typeof setInterval> | null };

export type PollClassification =
  | { kind: "ignore" }
  | { kind: "generating"; processing: boolean }
  | { kind: "done" };

export interface PollUntilGeneratedOptions<T> {
  /**
   * Shared timer slot. Every stop clears whatever interval this ref holds at
   * that moment, so a newer poll stored in the same ref is stopped too.
   */
  timerRef: PollTimerRef;
  intervalMs: number;
  /** Absolute tick ceiling, counted whether the job is queued or processing. */
  maxQueuedPolls: number;
  maxProcessingPolls: number;
  isCurrent: () => boolean;
  onTick?: (pollNumber: number) => void;
  poll: (pollNumber: number) => Promise<T>;
  classify: (result: T) => PollClassification;
  onGenerating?: (result: T) => void;
  onDone: (result: T) => void;
  /** Last attempt once the queued ceiling is passed. Resolve true if it applied data. */
  finalFetch: () => Promise<boolean>;
  onTimeout: () => void;
  onPollError?: (error: unknown) => void;
  onFinalFetchError?: (error: unknown) => void;
}

export function pollUntilGenerated<T>(options: PollUntilGeneratedOptions<T>): void {
  const { timerRef } = options;

  const stop = () => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
  };

  if (timerRef.current) {
    clearInterval(timerRef.current);
  }

  let processingPollCount = 0;
  let totalPollCount = 0;
  let pollInFlight = false;

  timerRef.current = setInterval(async () => {
    if (pollInFlight) return;
    totalPollCount++;

    if (!options.isCurrent() || totalPollCount > options.maxQueuedPolls) {
      stop();
      if (totalPollCount > options.maxQueuedPolls) {
        try {
          if (await options.finalFetch()) return;
        } catch (error) {
          options.onFinalFetchError?.(error);
        }
        options.onTimeout();
      }
      return;
    }

    pollInFlight = true;
    try {
      options.onTick?.(totalPollCount);
      const result = await options.poll(totalPollCount);
      const classification = options.classify(result);

      if (classification.kind === "ignore") return;

      if (classification.kind === "generating") {
        options.onGenerating?.(result);
        if (classification.processing) {
          processingPollCount++;
          if (processingPollCount > options.maxProcessingPolls) {
            stop();
            options.onTimeout();
          }
        }
        return;
      }

      stop();
      options.onDone(result);
    } catch (error) {
      options.onPollError?.(error);
    } finally {
      pollInFlight = false;
    }
  }, options.intervalMs);
}
